'use client';

import React, { useState, useMemo } from 'react';
import { Search, X, Download, Bookmark, CheckCircle, HardDrive, Film } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import CategoryIcon from '@/components/ui/CategoryIcon';

// ── Types ──────────────────────────────────────────────────────────────────────
interface ScenepackData {
  id: string;
  title: string;
  creator: string;
  creatorAvatar: string;
  verified: boolean;
  thumbnail: string;
  description: string;
  fileSize: string;
  clipCount: number;
  category: string;
  tags: string[];
  downloads: number;
  saves: number;
}

// ── Mock Data ──────────────────────────────────────────────────────────────────
const MOCK_SCENEPACKS: ScenepackData[] = [
{
  id: 'sp-1',
  title: 'Cinematic Anime Moments Vol. 1',
  creator: 'bytez_filmss',
  creatorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1512c0556-1785516006666.png",
  verified: true,
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1512c0556-1785516006666.png",
  description: 'A curated collection of high-impact anime scenes perfect for edits. Includes iconic moments from popular series.',
  fileSize: '2.4 GB',
  clipCount: 34,
  category: 'Anime',
  tags: ['anime', 'cinematic', 'edits'],
  downloads: 12400,
  saves: 3200
},
{
  id: 'sp-2',
  title: 'Action Movie Explosions Pack',
  creator: 'vfx_master',
  creatorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ff188152-1785516005776.png",
  verified: false,
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1ff188152-1785516005776.png",
  description: 'High-octane explosion and action sequences from blockbuster films. Perfect for hype edits and montages.',
  fileSize: '4.1 GB',
  clipCount: 52,
  category: 'Action',
  tags: ['action', 'explosions', 'hype'],
  downloads: 9800,
  saves: 2100
},
{
  id: 'sp-3',
  title: 'Cinematic Landscape Shots',
  creator: 'nature_lens',
  creatorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13f3bf54d-1772657149645.png",
  verified: true,
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_13f3bf54d-1772657149645.png",
  description: 'Breathtaking landscape and nature cinematography for ambient and travel edits.',
  fileSize: '3.7 GB',
  clipCount: 28,
  category: 'Nature',
  tags: ['nature', 'landscape', 'ambient'],
  downloads: 7600,
  saves: 1900
},
{
  id: 'sp-4',
  title: 'Gaming Highlights Megapack',
  creator: 'clip_god99',
  creatorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_103ccd694-1769268204956.png",
  verified: false,
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_103ccd694-1769268204956.png",
  description: 'Top-tier gaming moments and clutch plays from popular titles. Great for montage and reaction edits.',
  fileSize: '5.2 GB',
  clipCount: 67,
  category: 'Gaming',
  tags: ['gaming', 'highlights', 'montage'],
  downloads: 18300,
  saves: 5400
},
{
  id: 'sp-5',
  title: 'Sad Anime Scenes Collection',
  creator: 'emo_edits',
  creatorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13a89a8d1-1767897957482.png",
  verified: false,
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_13a89a8d1-1767897957482.png",
  description: 'Emotional and melancholic anime scenes for AMVs and sad edits. Carefully selected for maximum impact.',
  fileSize: '1.8 GB',
  clipCount: 22,
  category: 'Anime',
  tags: ['anime', 'sad', 'amv'],
  downloads: 14700,
  saves: 4800
},
{
  id: 'sp-6',
  title: 'Sports Hype Reel Pack',
  creator: 'sports_cuts',
  creatorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1df975d23-1785516006939.png",
  verified: true,
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1df975d23-1785516006939.png",
  description: 'Intense sports moments, celebrations, and athletic feats from various disciplines.',
  fileSize: '3.3 GB',
  clipCount: 41,
  category: 'Sports',
  tags: ['sports', 'hype', 'athletic'],
  downloads: 6200,
  saves: 1400
},
{
  id: 'sp-7',
  title: 'VFX Transitions & Effects',
  creator: 'vfx_master',
  creatorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d9d29f16-1785516006349.png",
  verified: false,
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1d9d29f16-1785516006349.png",
  description: 'Professional VFX transitions, overlays, and effects for advanced video editing projects.',
  fileSize: '6.8 GB',
  clipCount: 89,
  category: 'VFX',
  tags: ['vfx', 'transitions', 'effects'],
  downloads: 22100,
  saves: 7300
},
{
  id: 'sp-8',
  title: 'Music Video Aesthetics Pack',
  creator: 'mv_curator',
  creatorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1adf5f86d-1764655704801.png",
  verified: true,
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1adf5f86d-1764655704801.png",
  description: 'Visually striking music video clips with unique aesthetics and color grading for creative edits.',
  fileSize: '2.9 GB',
  clipCount: 38,
  category: 'Music Videos',
  tags: ['music', 'aesthetic', 'colorful'],
  downloads: 8900,
  saves: 2700
},
{
  id: 'sp-9',
  title: 'Classic Movie Monologues',
  creator: 'cinema_archive',
  creatorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18f74f3cc-1766855669121.png",
  verified: true,
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_18f74f3cc-1766855669121.png",
  description: 'Iconic monologues and dramatic scenes from classic and contemporary cinema.',
  fileSize: '1.5 GB',
  clipCount: 18,
  category: 'Movies',
  tags: ['movies', 'monologue', 'drama'],
  downloads: 5400,
  saves: 1600
},
{
  id: 'sp-10',
  title: 'Meme Compilation Goldmine',
  creator: 'meme_lord',
  creatorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18b382450-1765047260851.png",
  verified: false,
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_18b382450-1765047260851.png",
  description: 'The ultimate meme clip collection. Viral moments, reaction clips, and internet classics.',
  fileSize: '0.9 GB',
  clipCount: 112,
  category: 'Memes',
  tags: ['memes', 'viral', 'funny'],
  downloads: 31500,
  saves: 9200
},
{
  id: 'sp-11',
  title: 'TV Drama Intense Scenes',
  creator: 'tv_edits',
  creatorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18f74f3cc-1766855669121.png",
  verified: false,
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_18f74f3cc-1766855669121.png",
  description: 'Gripping TV drama moments, plot twists, and emotional scenes from top-rated series.',
  fileSize: '2.1 GB',
  clipCount: 29,
  category: 'TV Shows',
  tags: ['tv', 'drama', 'intense'],
  downloads: 7100,
  saves: 2000
},
{
  id: 'sp-12',
  title: 'Car Chase & Racing Scenes',
  creator: 'speed_edits',
  creatorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18ed33963-1785516007413.png",
  verified: true,
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_18ed33963-1785516007413.png",
  description: 'High-speed car chases, racing footage, and automotive cinematography for adrenaline edits.',
  fileSize: '4.6 GB',
  clipCount: 45,
  category: 'Cars',
  tags: ['cars', 'racing', 'speed'],
  downloads: 10200,
  saves: 2900
}];


const CATEGORIES = [
  { key: 'all',          label: 'All' },
  { key: 'Action',       label: 'Action' },
  { key: 'Anime',        label: 'Anime' },
  { key: 'Cinematic',    label: 'Cinematic' },
  { key: 'Movies',       label: 'Movies' },
  { key: 'TV Shows',     label: 'TV Shows' },
  { key: 'Gaming',       label: 'Gaming' },
  { key: 'Cars',         label: 'Cars' },
  { key: 'Nature',       label: 'Nature' },
  { key: 'Sports',       label: 'Sports' },
  { key: 'Music Videos', label: 'Music' },
  { key: 'Memes',        label: 'Memes' },
  { key: 'VFX',          label: 'VFX' },
];


function formatCount(n: number): string {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
}

// ── ScenepackCard ──────────────────────────────────────────────────────────────
function ScenepackCard({ pack }: {pack: ScenepackData;}) {
  const [saved, setSaved] = useState(false);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="clip-card-hover rounded-xl overflow-hidden cursor-pointer group"
      style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      
      {/* Thumbnail */}
      <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
        <AppImage
          src={pack.thumbnail}
          alt={`${pack.title} scenepack thumbnail — ${pack.clipCount} clips by ${pack.creator}`}
          fill
          className="object-cover transition-transform duration-300 group-hover:scale-105"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" />
        

        {/* Hover overlay */}
        <div
          className="absolute inset-0 flex items-center justify-center transition-opacity duration-200"
          style={{ background: 'rgba(0,0,0,0.5)', opacity: hovered ? 1 : 0 }}>
          
          <span
            className="px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2"
            style={{ background: 'rgba(108, 99, 255, 0.9)', color: 'white' }}>
            
            <Download size={14} />
            Download Pack
          </span>
        </div>

        {/* Clip count badge */}
        <span
          className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold font-mono"
          style={{ background: 'rgba(0,0,0,0.75)', color: 'white' }}>
          
          {pack.clipCount} clips
        </span>

        {/* Category badge */}
        <div className="absolute top-2 left-2">
          <span
            className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md"
            style={{ background: 'rgba(108,99,255,0.85)', color: 'white' }}
          >
            <CategoryIcon category={pack.category} size={10} color="white" />
            {pack.category}
          </span>
        </div>

        {/* Save button */}
        <button
          onClick={(e) => {e.stopPropagation();setSaved(!saved);}}
          className="absolute bottom-2 left-2 w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150 hover:scale-110"
          style={{
            background: saved ? 'rgba(108, 99, 255, 0.9)' : 'rgba(0,0,0,0.6)',
            color: saved ? 'white' : 'rgba(255,255,255,0.7)'
          }}
          title={saved ? 'Remove from saved' : 'Save pack'}>
          
          <Bookmark size={12} fill={saved ? 'currentColor' : 'none'} />
        </button>
      </div>

      {/* Card Body */}
      <div className="p-3">
        {/* Title */}
        <h3
          className="font-semibold text-sm leading-snug mb-1.5 line-clamp-1 transition-colors duration-150 group-hover:text-primary"
          style={{ color: 'var(--foreground)' }}>
          
          {pack.title}
        </h3>

        {/* Description */}
        <p
          className="text-xs leading-relaxed mb-2.5 line-clamp-2"
          style={{ color: 'var(--muted-foreground)' }}>
          
          {pack.description}
        </p>

        {/* Creator */}
        <div className="flex items-center gap-2 mb-3">
          <div className="relative w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
            <AppImage
              src={pack.creatorAvatar}
              alt={`${pack.creator} profile picture`}
              fill
              className="object-cover"
              sizes="20px" />
            
          </div>
          <span className="text-xs font-medium truncate" style={{ color: 'var(--muted-foreground)' }}>
            {pack.creator}
          </span>
          {pack.verified &&
          <CheckCircle size={12} style={{ color: 'var(--verified)', flexShrink: 0 }} />
          }
        </div>

        {/* File size + stats row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5" style={{ color: 'var(--muted-foreground)' }}>
            <HardDrive size={12} />
            <span className="text-xs font-semibold font-mono" style={{ color: 'var(--accent)' }}>
              {pack.fileSize}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
              <Download size={12} />
              <span className="text-xs tabular-nums font-medium">{formatCount(pack.downloads)}</span>
            </div>
            <div className="flex items-center gap-1" style={{ color: saved ? 'var(--primary)' : 'var(--muted-foreground)' }}>
              <Bookmark size={12} fill={saved ? 'currentColor' : 'none'} />
              <span className="text-xs tabular-nums font-medium">{formatCount(pack.saves + (saved ? 1 : 0))}</span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <button
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all duration-150 hover:opacity-90 active:scale-95"
            style={{ background: 'linear-gradient(135deg, var(--primary) 0%, #5B54E8 100%)', color: 'white' }}>
            
            <Download size={13} />
            Download
          </button>
          <button
            onClick={(e) => {e.stopPropagation();setSaved(!saved);}}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all duration-150"
            style={{
              background: saved ? 'rgba(108,99,255,0.15)' : 'var(--muted)',
              color: saved ? 'var(--primary)' : 'var(--muted-foreground)',
              border: `1px solid ${saved ? 'rgba(108,99,255,0.4)' : 'var(--border)'}`
            }}>
            
            <Bookmark size={13} fill={saved ? 'currentColor' : 'none'} />
            {saved ? 'Saved' : 'Save'}
          </button>
        </div>
      </div>
    </div>);

}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function ScenepacksContent() {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const results = useMemo(() => {
    return MOCK_SCENEPACKS.filter((pack) => {
      const matchesCategory = activeCategory === 'all' || pack.category === activeCategory;
      if (!matchesCategory) return false;
      if (!query.trim()) return true;
      const lower = query.toLowerCase();
      return (
        pack.title.toLowerCase().includes(lower) ||
        pack.creator.toLowerCase().includes(lower) ||
        pack.description.toLowerCase().includes(lower) ||
        pack.tags.some((t) => t.toLowerCase().includes(lower)));

    });
  }, [query, activeCategory]);

  return (
    <main className="max-w-screen-2xl mx-auto px-4 pb-28 pt-6">
      {/* Page heading */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight mb-1" style={{ color: 'var(--foreground)' }}>
          Scene Packs
        </h1>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Curated clip collections ready to download — search by title, creator, or category
        </p>
      </div>

      {/* Search bar */}
      <div className="relative mb-5">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search scenepacks, creators, tags…"
          className="input-field w-full pl-11 pr-11 py-3 text-sm" />
        
        {query &&
        <button
          onClick={() => setQuery('')}
          className="absolute right-4 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-80"
          style={{ color: 'var(--muted-foreground)' }}>
          
            <X size={15} />
          </button>
        }
      </div>

      {/* Category chips */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 mb-6" style={{ scrollbarWidth: 'none' }}>
        {CATEGORIES.map((cat) =>
        <button
          key={cat.key}
          onClick={() => setActiveCategory(cat.key)}
          className="category-chip flex items-center gap-1.5 flex-shrink-0"
          style={
          activeCategory === cat.key ?
          { background: 'var(--primary)', color: '#fff', borderColor: 'var(--primary)' } :
          {}
          }>
            <CategoryIcon category={cat.key} size={13} />
            {cat.label}
          </button>
        )}
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-medium" style={{ color: 'var(--muted-foreground)' }}>
          {results.length} {results.length === 1 ? 'pack' : 'packs'} found
          {query &&
          <span> for <span style={{ color: 'var(--foreground)' }}>"{query}"</span></span>
          }
        </p>
      </div>

      {/* Grid */}
      {results.length > 0 ?
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {results.map((pack) =>
        <ScenepackCard key={pack.id} pack={pack} />
        )}
        </div> :

      <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ background: 'var(--muted)' }}>
          
            <Film size={28} style={{ color: 'var(--muted-foreground)' }} />
          </div>
          <div className="text-center">
            <p className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>No packs found</p>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Try a different search term or category
            </p>
          </div>
          <button
          onClick={() => {setQuery('');setActiveCategory('all');}}
          className="btn-primary px-5 py-2 text-sm">
          
            Clear filters
          </button>
        </div>
      }
    </main>);

}